import { useAsyncFn } from 'react-use';

import {
  configApiRef,
  fetchApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';

import { KaapTenant } from './types';

/** The snake_case tenant payload the KaaP API returns. */
type KaapTenantPayload = {
  id?: string;
  name: string;
  cluster_id?: string | null;
  owner_object_id?: string;
  pim_enabled?: boolean;
  pim_group_id?: string | null;
};

/**
 * Maps a KaaP API tenant onto the shape a DaaS project stores on its clusters.
 *
 * `clusterId` is `<subscription name>/<cluster name>`; both halves are split out
 * because `tomtom:generate-daas-clusters-object` resolves the cluster entity by
 * title and subscription name.
 */
export const deserializeKaapTenant = (
  payload: KaapTenantPayload,
): KaapTenant => ({
  id: payload.id,
  name: payload.name,
  clusterId: payload.cluster_id ?? null,
  clusterSubscriptionName: payload.cluster_id?.split('/')[0] ?? '',
  clusterName: payload.cluster_id?.split('/')[1] ?? '',
  ownerId: payload.owner_object_id,
  pimEnabled: payload.pim_enabled ?? false,
  pimGroupId: payload.pim_group_id ?? null,
});

/**
 * Lazily fetches the KaaP tenants a DaaS project can bind a cluster to.
 *
 * The KaaP API is the only source of `pimEnabled` / `pimGroupId` - the catalog
 * `kaap-tenant` resources do not carry them - and the project template turns
 * `pimGroupId` into the namespace's AAD group binding, so the picker has to ask
 * KaaP directly. Returns a `useAsyncFn` pair so the PIM status can be re-polled
 * while a tenant's PIM group is still being provisioned.
 */
export const useGetKaapTenants = () => {
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const microsoftAuth = useApi(microsoftAuthApiRef);

  // Read optionally and checked inside the callback: `getString` throws, and
  // throwing here would take the whole project form down at render time
  // instead of reporting one broken picker through `errorApi`.
  const apiUrl = configApi.getOptionalString('kaap.apiUrl');

  return useAsyncFn(async (): Promise<KaapTenant[]> => {
    if (!apiUrl) {
      throw new Error(
        'KaaP tenants are unavailable: `kaap.apiUrl` is not configured',
      );
    }

    const token = await microsoftAuth.getAccessToken(`${apiUrl}/.default`);
    const response = await fetchApi.fetch(`${apiUrl}/v1/tenants`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch KaaP tenants: ${response.status} ${response.statusText}`,
      );
    }

    const { tenants } = await response.json();
    return ((tenants ?? []) as KaapTenantPayload[]).map(deserializeKaapTenant);
  }, [apiUrl, fetchApi, microsoftAuth]);
};
